import { useCallback, useState } from 'react'
import {
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import type { DragEndEvent, DragOverEvent, DragStartEvent } from '@dnd-kit/core'
import type { DraftPlayer } from '../types'

// ── ID scheme ─────────────────────────────────────────────────────────────────
// Filled slots (pitch starter, bench outfield, bench GK) are draggable AND droppable
// under a STABLE, player-based id: "player-{playerId}". Empty drop targets keep
// position/slot ids so we know where the drop landed:
//   Empty pitch circle:      "empty-{POS}-{i}"
//   Empty outfield bench:    "bench-{i}"            (i = 0,1,2)
// Using a stable id per player (instead of an array index) keeps dnd-kit's drop
// animation correct after a reorder: the dragged player's id always resolves to its
// real new DOM node, so the ghost snaps to the right place — no phantom slide-in.

function parsePlayerId(id: string): string | null {
  return id.startsWith('player-') ? id.slice('player-'.length) : null
}

function parseEmptyBenchIndex(id: string): number | null {
  const m = id.match(/^bench-(\d+)$/)
  return m ? parseInt(m[1] ?? '0', 10) : null
}

function isEmptyPitchSlot(id: string): boolean {
  return /^empty-(GK|DEF|MID|FWD)-\d+$/.test(id)
}

type PlayerLocation =
  | { zone: 'starter'; index: number }
  | { zone: 'bench';   index: number }
  | { zone: 'benchGk' }

// ── Hook ──────────────────────────────────────────────────────────────────────

type UseSquadDndParams = {
  starters:             readonly DraftPlayer[]
  bench:                readonly DraftPlayer[]
  benchGk:              DraftPlayer | null
  reorderBench:         (fromIndex: number, toIndex: number) => void
  swapStarterWithBench: (starterIndex: number, benchIndex: number) => void
  swapStarters:         (indexA: number, indexB: number) => void
  swapGks:              () => void
  promoteBench:         (benchIndex: number) => void
  demoteStarter:        (starterIndex: number, benchIndex: number) => void
}

export function useSquadDnd({
  starters,
  bench,
  benchGk,
  reorderBench,
  swapStarterWithBench,
  swapStarters,
  swapGks,
  promoteBench,
  demoteStarter,
}: UseSquadDndParams) {
  const [activeId, setActiveId] = useState<string | null>(null)
  const [overId,   setOverId]   = useState<string | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(String(event.active.id))
    setOverId(null)
  }, [])

  const handleDragOver = useCallback((event: DragOverEvent) => {
    setOverId(event.over ? String(event.over.id) : null)
  }, [])

  // Resolve where a player currently sits in the draft, by stable id.
  const locate = useCallback((playerId: string): PlayerLocation | null => {
    const sIdx = starters.findIndex(p => p.playerId === playerId)
    if (sIdx !== -1) return { zone: 'starter', index: sIdx }
    const bIdx = bench.findIndex(p => p.playerId === playerId)
    if (bIdx !== -1) return { zone: 'bench', index: bIdx }
    if (benchGk?.playerId === playerId) return { zone: 'benchGk' }
    return null
  }, [starters, bench, benchGk])

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    setActiveId(null)
    setOverId(null)

    const activeId = String(event.active.id)
    const overId   = event.over ? String(event.over.id) : null
    if (!overId || activeId === overId) return

    const activePlayerId = parsePlayerId(activeId)
    if (activePlayerId === null) return            // only players are draggable
    const from = locate(activePlayerId)
    if (from === null) return

    // Resolve the drop target: another player (filled slot), or an empty slot.
    const overPlayerId = parsePlayerId(overId)
    const over         = overPlayerId !== null ? locate(overPlayerId) : null
    const overEmptyBenchIdx = parseEmptyBenchIndex(overId)
    const overEmptyPitch    = isEmptyPitchSlot(overId)

    // GK swap: the bench GK only ever swaps with the starting GK (either side may be the GK).
    if (from.zone === 'benchGk' || over?.zone === 'benchGk') {
      swapGks()
      return
    }

    if (from.zone === 'bench') {
      // bench → empty pitch circle: promote into the XI. An empty circle is just an open
      // starting spot — accept a bench player of ANY position. promoteBench drops them into
      // their own band; squad position maxes keep the resulting XI within limits.
      if (overEmptyPitch)              { promoteBench(from.index); return }
      // bench → filled starter: swap the two.
      if (over?.zone === 'starter')    { swapStarterWithBench(over.index, from.index); return }
      // bench → bench (filled or empty): reorder priority.
      if (over?.zone === 'bench')      { reorderBench(from.index, over.index); return }
      if (overEmptyBenchIdx !== null)  { reorderBench(from.index, overEmptyBenchIdx); return }
      return
    }

    // from.zone === 'starter'
    // starter → filled bench: swap the two.
    if (over?.zone === 'bench')        { swapStarterWithBench(from.index, over.index); return }
    // starter → empty bench slot: demote into the bench (leaving an empty XI slot).
    if (overEmptyBenchIdx !== null)    { demoteStarter(from.index, overEmptyBenchIdx); return }
    // starter → filled starter: swap.
    if (over?.zone === 'starter' && over.index !== from.index) { swapStarters(from.index, over.index); return }
    // starter → empty pitch circle: no-op (the player is already in the XI).
  }, [locate, reorderBench, swapStarterWithBench, swapStarters, swapGks, promoteBench, demoteStarter])

  const handleDragCancel = useCallback(() => {
    setActiveId(null)
    setOverId(null)
  }, [])

  // Resolve the player currently being dragged for the DragOverlay ghost
  function resolveActiveDragPlayer(): DraftPlayer | null {
    if (!activeId) return null
    const playerId = parsePlayerId(activeId)
    if (playerId === null) return null
    const loc = locate(playerId)
    if (loc === null) return null
    if (loc.zone === 'starter') return starters[loc.index] ?? null
    if (loc.zone === 'bench')   return bench[loc.index] ?? null
    return benchGk
  }

  return {
    sensors,
    activeId,
    overId,
    activeDragPlayer: resolveActiveDragPlayer(),
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleDragCancel,
  }
}
